/**
 * Gestión de destinatarios de notificación para un cliente.
 *
 * Lista los configs existentes, permite agregar uno nuevo (modal inline) o
 * borrar uno. Cada destinatario tiene canal + dirección + filtro de
 * severidades (vacío = todas).
 */
import { useState } from "react";
import { Bell, Mail, MessageSquare, Plus, Trash2, X } from "lucide-react";
import {
  useNotificationConfigs,
  useCreateNotificationConfig,
  useDeleteNotificationConfig,
  type NotificationChannel,
  type NotificationConfig,
  type UrgencyLevel,
} from "@/hooks/use-notification-configs";
import { cn } from "@/lib/cn";
import type { ClientSlug } from "@/types";

const CHANNEL_LABEL: Record<NotificationChannel, string> = {
  mail: "Email",
  whatsapp: "WhatsApp",
  sms: "SMS",
  slack: "Slack",
  discord: "Discord",
  call: "Llamada",
};

const CHANNEL_AVAILABLE: NotificationChannel[] = ["mail"];
// Los demás existen en el schema pero todavía no hay sender configurado.

const SEVERITY_LABEL: Record<UrgencyLevel, string> = {
  critical: "Crítica",
  high: "Alta",
  medium: "Media",
  low: "Baja",
};

const SEVERITY_ORDER: UrgencyLevel[] = ["critical", "high", "medium", "low"];

export function NotificationRecipients({ clientSlug }: { clientSlug: ClientSlug }) {
  const { data: configs, isLoading } = useNotificationConfigs(clientSlug);
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="card-surface p-4 mb-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Bell size={14} strokeWidth={1.75} className="text-text-muted" />
          <h2 className="font-sans text-sm font-semibold text-text-primary">
            Notificaciones automáticas
          </h2>
        </div>
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-default text-text-secondary hover:bg-surface-2 hover:text-text-primary font-sans text-xs font-medium transition-colors"
          >
            <Plus size={13} strokeWidth={2} />
            Agregar destinatario
          </button>
        )}
      </div>

      {isLoading ? (
        <p className="font-sans text-xs text-text-muted">Cargando destinatarios…</p>
      ) : !configs?.length ? (
        <p className="font-sans text-xs text-text-muted">
          Sin destinatarios configurados. Cuando se crea una alerta, no se notifica a nadie por fuera del panel — agregá un email para que llegue a alguien específico.
        </p>
      ) : (
        <ul className="space-y-2">
          {configs.map((cfg) => (
            <RecipientRow key={cfg.id} config={cfg} />
          ))}
        </ul>
      )}

      {showForm && (
        <RecipientForm
          clientSlug={clientSlug}
          onClose={() => setShowForm(false)}
        />
      )}
    </div>
  );
}

function RecipientRow({ config }: { config: NotificationConfig }) {
  const del = useDeleteNotificationConfig();
  const channelIcon =
    config.channel === "mail" ? (
      <Mail size={13} strokeWidth={1.75} />
    ) : (
      <MessageSquare size={13} strokeWidth={1.75} />
    );

  const severities =
    config.urgencyLevels.length === 0
      ? "todas"
      : SEVERITY_ORDER.filter((s) => config.urgencyLevels.includes(s))
          .map((s) => SEVERITY_LABEL[s].toLowerCase())
          .join(", ");

  return (
    <li className="flex items-center justify-between gap-3 rounded-lg bg-surface-2 px-3 py-2">
      <div className="flex items-center gap-2 min-w-0">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-3 px-2 py-0.5 text-2xs font-sans font-medium text-text-secondary">
          {channelIcon}
          {CHANNEL_LABEL[config.channel]}
        </span>
        <span className="font-sans text-sm text-text-primary truncate">
          {config.destination}
        </span>
        <span className="font-sans text-xs text-text-muted">
          · severidades {severities}
        </span>
      </div>
      <button
        type="button"
        onClick={() => del.mutate({ id: config.id })}
        disabled={del.isPending}
        aria-label="Eliminar destinatario"
        className="inline-flex items-center justify-center w-7 h-7 rounded-md text-text-muted hover:text-error-text hover:bg-error-bg transition-colors disabled:opacity-50"
      >
        <Trash2 size={13} strokeWidth={1.75} />
      </button>
    </li>
  );
}

function RecipientForm({
  clientSlug,
  onClose,
}: {
  clientSlug: ClientSlug;
  onClose: () => void;
}) {
  const [channel, setChannel] = useState<NotificationChannel>("mail");
  const [destination, setDestination] = useState("");
  const [selectedSeverities, setSelectedSeverities] = useState<Set<UrgencyLevel>>(
    new Set(["critical", "high"]),
  );
  const create = useCreateNotificationConfig();

  function toggleSeverity(sev: UrgencyLevel) {
    setSelectedSeverities((prev) => {
      const next = new Set(prev);
      if (next.has(sev)) next.delete(sev);
      else next.add(sev);
      return next;
    });
  }

  function handleSubmit() {
    if (!destination.trim()) return;
    create.mutate(
      {
        clientSlug,
        channel,
        destination: destination.trim(),
        urgencyLevels: Array.from(selectedSeverities),
      },
      {
        onSuccess: () => {
          setDestination("");
          onClose();
        },
      },
    );
  }

  const validEmail =
    channel === "mail" ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(destination) : true;
  const canSubmit = destination.trim() && validEmail && !create.isPending;

  return (
    <div className="mt-3 pt-3 border-t border-subtle">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-sans text-xs font-semibold text-text-primary">
          Nuevo destinatario
        </h3>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar"
          className="inline-flex items-center justify-center w-6 h-6 rounded-md text-text-muted hover:text-text-primary hover:bg-surface-2 transition-colors"
        >
          <X size={12} strokeWidth={2} />
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-2 mb-3">
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value as NotificationChannel)}
          aria-label="Canal"
          className="h-8 px-2 text-xs font-sans rounded-md bg-surface-2 border border-default text-text-primary focus:border-magenta focus:outline-none"
        >
          {CHANNEL_AVAILABLE.map((ch) => (
            <option key={ch} value={ch}>
              {CHANNEL_LABEL[ch]}
            </option>
          ))}
        </select>
        <input
          type={channel === "mail" ? "email" : "text"}
          value={destination}
          onChange={(e) => setDestination(e.target.value)}
          placeholder={
            channel === "mail" ? "nombre@champe.com.ar" : "número o handle"
          }
          className="h-8 px-3 text-sm font-sans rounded-md bg-surface-2 border border-default text-text-primary placeholder:text-text-muted focus:border-magenta focus:outline-none"
        />
      </div>

      <div className="mb-3">
        <div className="font-sans text-2xs uppercase tracking-wider text-text-muted mb-1.5">
          Avisar para severidades
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          {SEVERITY_ORDER.map((sev) => {
            const active = selectedSeverities.has(sev);
            return (
              <button
                key={sev}
                type="button"
                onClick={() => toggleSeverity(sev)}
                className={cn(
                  "inline-flex items-center px-2.5 py-1 rounded-full text-2xs font-sans font-medium border transition-colors",
                  active
                    ? "border-magenta bg-state-human-bg text-magenta"
                    : "border-default bg-surface-2 text-text-secondary hover:bg-surface-3",
                )}
              >
                {SEVERITY_LABEL[sev]}
              </button>
            );
          })}
        </div>
        {selectedSeverities.size === 0 && (
          <p className="font-sans text-2xs text-text-muted mt-1.5">
            Sin filtro = avisa para todas las severidades.
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className={cn(
            "h-8 px-3 rounded-lg text-xs font-sans font-medium",
            "bg-magenta text-white hover:bg-magenta-hover transition-colors",
            "disabled:opacity-50 disabled:cursor-not-allowed",
          )}
        >
          {create.isPending ? "Guardando…" : "Guardar destinatario"}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="h-8 px-3 rounded-lg text-xs font-sans text-text-muted hover:text-text-primary transition-colors"
        >
          Cancelar
        </button>
        {channel === "mail" && destination && !validEmail && (
          <span className="font-sans text-2xs text-warning-text">
            Email inválido
          </span>
        )}
      </div>
    </div>
  );
}
